// components/TablaMejorasEtapaI.jsx - Seguimiento Mejoras Etapa I
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import DatePicker from 'react-datepicker';
import InfoButtonWithPopover from "./InfoButtonWithPopover";
import NotaPdfUpload from "./NotaPdfUpload";
import { AuthContext } from "../context/authContext";
import { ROLES_ALERTA } from "../hooks/useAlertasMejoramientos";
import { initiateSocketConnection } from "../service/socketio";
import { pedirAccionAlerta } from "../utils/alertasMejoramientos";

const parseLocalDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput === 'string') {
        if (dateInput.includes('T')) {
            const dateObj = new Date(dateInput);
            const userTimezoneOffset = dateObj.getTimezoneOffset() * 60000;
            return new Date(dateObj.getTime() + userTimezoneOffset);
        }
        const parts = dateInput.split('T')[0].split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);
            return new Date(year, month, day);
        }
    }
    return new Date(dateInput);
};

const hoyLocalDate = () => {
    const hoy = new Date();
    return new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
};

const TablaMejorasEtapaI = ({
    mejorasEtapaI = [],
    onMejorasChange,
    codigoObra = '',
    etapa = 'i',
    titulo = "Seguimiento de Intercambios (Mejoras - Etapa I)",
    tooltip = "Registro dinámico de intercambios de entrega/recepción. Permite documentar cada fecha, el N° de PV, la descripción del intercambio, el PDF de la nota, el estado (Enviado / Recibido / Sin estado) y la marca de Solicitud de prórroga (necesaria para cerrar el ciclo 1 de la alerta) para el seguimiento de la Etapa I."
}) => {

    const { token, role, roles } = useContext(AuthContext);

    // Mismos roles que la burbuja de Alertas: son los que pueden enviar los
    // mails y resolver el ciclo. Para el resto la tabla funciona igual, sin
    // las acciones de alertas.
    const puedeAlertas = useMemo(() => {
        if (!role) return false;
        if (Array.isArray(roles) && roles.some((r) => ROLES_ALERTA.includes(r))) return true;
        return ROLES_ALERTA.includes(role);
    }, [role, roles]);

    // Estado de la alerta de esta obra. Se usa para mostrar la acción de
    // notificación (solo si hay una ventana vencida sin notificar) y la de
    // respuesta de prórroga (solo con el ciclo 1 pendiente y ya notificado).
    const [alertaEstado, setAlertaEstado] = useState(null);

    const cargarAlertaEstado = useCallback(async () => {
        if (!puedeAlertas || !codigoObra || !token) {
            setAlertaEstado(null);
            return;
        }
        try {
            const res = await fetch(`/api/alertas-mejoramientos/obra/${encodeURIComponent(codigoObra)}`, {
                headers: { Authorization: `Bearer ${token}` },
                cache: 'no-store',
            });
            if (!res.ok) {
                setAlertaEstado(null);
                return;
            }
            const data = await res.json().catch(() => ({}));
            setAlertaEstado(data?.data ?? null);
        } catch (e) {
            console.error('[seguimiento mejoras] error al consultar la alerta:', e);
            setAlertaEstado(null);
        }
    }, [puedeAlertas, codigoObra, token]);

    useEffect(() => {
        cargarAlertaEstado();
    }, [cargarAlertaEstado]);

    // Si se envía la notificación o se resuelve el ciclo desde la burbuja, el
    // backend avisa por socket: refrescamos el estado para que el banner y el
    // botón de respuesta se actualicen sin recargar la obra.
    useEffect(() => {
        if (!puedeAlertas || !codigoObra) return undefined;
        const s = initiateSocketConnection();
        const onActualizada = () => { cargarAlertaEstado(); };
        s.on('alertas_mejoramientos_actualizadas', onActualizada);
        return () => { s.off('alertas_mejoramientos_actualizadas', onActualizada); };
    }, [puedeAlertas, codigoObra, cargarAlertaEstado]);

    const pendiente = alertaEstado?.pendiente || null;
    const notificacionEnviada = Boolean(pendiente?.notificacionEnviadaEn);
    const esCiclo1Pendiente = Boolean(pendiente && Number(pendiente.ciclo) === 1);
    const fechaDia = (valor) => {
        if (!valor) return '—';
        const [y, m, d] = String(valor).slice(0, 10).split('-');
        return y && m && d ? `${d}/${m}/${y}` : String(valor);
    };

    // Handler para agregar un nuevo registro a la tabla
    const handleAgregarRegistro = useCallback(() => {
        const nuevoRegistro = {
            fecha: null,
            nro_pv: '',
            descripcion: '',
            estado: 'Enviado' // Valor inicial por defecto
        };
        onMejorasChange(prev => [...prev, nuevoRegistro]);
    }, [onMejorasChange]);

    // Handler para eliminar un registro
    const handleEliminarRegistro = useCallback((index) => {
        const confirmar = window.confirm(
            `⚠️ ¿Está seguro de que desea eliminar este registro de seguimiento?`
        );
        if (!confirmar) return;

        onMejorasChange(prev => prev.filter((_, i) => i !== index));
    }, [onMejorasChange]);

    // Handler para cambiar valores de un registro específico
    const handleRegistroChange = useCallback((index, field, value) => {
        onMejorasChange(prev => {
            const nuevosRegistros = [...prev];
            nuevosRegistros[index] = {
                ...nuevosRegistros[index],
                [field]: value
            };
            return nuevosRegistros;
        });
    }, [onMejorasChange]);

    // Cambiar el estado. Si deja de ser 'Recibido', se destilda la solicitud de
    // prórroga: ese flag solo tiene sentido en una nota recibida.
    const handleEstadoChange = useCallback((index, value) => {
        onMejorasChange(prev => {
            const nuevosRegistros = [...prev];
            nuevosRegistros[index] = {
                ...nuevosRegistros[index],
                estado: value,
                ...(value !== 'Recibido' ? { solicitud_prorroga: false } : {}),
            };
            return nuevosRegistros;
        });
    }, [onMejorasChange]);

    // Al marcar la solicitud de prórroga se completa la fecha si estaba vacía:
    // el criterio del backend exige que la nota recibida sea posterior a la
    // notificación, y una fila sin fecha nunca habilitaba el cierre del ciclo 1.
    const handleSolicitudProrrogaChange = useCallback((index, checked) => {
        onMejorasChange(prev => {
            const nuevosRegistros = [...prev];
            const actual = nuevosRegistros[index];
            nuevosRegistros[index] = {
                ...actual,
                solicitud_prorroga: checked,
                ...(checked && !actual.fecha ? { fecha: hoyLocalDate() } : {}),
            };
            return nuevosRegistros;
        });
    }, [onMejorasChange]);

    // Clases de estilo consistentes
    const inputClasses = "w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all";

    return (
        <div className="mt-6 mb-6">
            <div className="flex justify-between items-center mb-3">
                <div className="flex flex-row items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                        {titulo}
                    </h3>
                    <InfoButtonWithPopover
                        infoText={tooltip}
                    />
                </div>
                <button
                    type="button"
                    onClick={handleAgregarRegistro}
                    className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm font-medium transition-colors shadow-sm"
                >
                    + Agregar Intercambio
                </button>
            </div>

            {puedeAlertas && pendiente && !notificacionEnviada && (
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-700 dark:bg-amber-900/20">
                    <p className="text-sm text-amber-800 dark:text-amber-300">
                        Ciclo {pendiente.ciclo} vencido el {fechaDia(pendiente.fechaVencimiento)}: todavía no se envió la notificación.
                    </p>
                    <button
                        type="button"
                        onClick={() => pedirAccionAlerta({ codigoObra, accion: 'notificacion' })}
                        className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium bg-amber-500 text-white hover:bg-amber-600 transition-colors"
                    >
                        Enviar notificación
                    </button>
                </div>
            )}

            {puedeAlertas && notificacionEnviada && esCiclo1Pendiente && pendiente?.prorrogaSolicitada && (
                <div className="mb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-blue-300 bg-blue-50 px-3 py-2 dark:border-blue-700 dark:bg-blue-900/20">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                        La organización solicitó una prórroga el {fechaDia(pendiente.prorrogaSolicitadaEn)}. Enviá el mail con el plazo otorgado para cerrar el ciclo 1.
                    </p>
                    <button
                        type="button"
                        onClick={() => pedirAccionAlerta({ codigoObra, accion: 'respuesta_ciclo1' })}
                        className="shrink-0 px-3 py-1.5 rounded-md text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
                    >
                        Enviar el mail con la prórroga
                    </button>
                </div>
            )}

            {mejorasEtapaI.length === 0 ? (
                <div className="p-6 bg-gray-50 dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 text-center text-gray-500 dark:text-gray-400">
                    No hay intercambios registrados. Haga clic en "+ Agregar Intercambio" para añadir un registro.
                </div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Tabla de Intercambios */}
                    <div className="overflow-x-auto lg:col-span-2">
                        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden shadow-sm">
                            <thead>
                                <tr className="bg-gray-100 dark:bg-gray-700">
                                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 w-16 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        N°
                                    </th>
                                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 w-44 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Fecha
                                    </th>
                                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 w-56 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Nro PV
                                    </th>
                                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Descripción del Intercambio
                                    </th>
                                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 w-36 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                                        Estado
                                    </th>
                                    <th className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-200 w-56">
                                        Acción
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {mejorasEtapaI.map((item, index) => {
                                    const fechaSeleccionada = parseLocalDate(item.fecha);

                                    return (
                                        <tr key={item.nota_ref || `fila-${index}`} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 text-center">
                                                <span className="font-medium text-gray-800 dark:text-white">
                                                    {index + 1}
                                                </span>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                                <DatePicker
                                                    selected={fechaSeleccionada}
                                                    onChange={(date) => handleRegistroChange(index, 'fecha', date)}
                                                    dateFormat="dd/MM/yyyy"
                                                    className={inputClasses}
                                                    placeholderText="DD/MM/AAAA"
                                                    showYearDropdown
                                                    scrollableYearDropdown
                                                    yearDropdownItemNumber={15}
                                                />
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                                <input
                                                    type="text"
                                                    value={item.nro_pv || ''}
                                                    onChange={(e) => handleRegistroChange(index, 'nro_pv', e.target.value)}
                                                    className={inputClasses}
                                                    placeholder="N° PV"
                                                    maxLength="100"
                                                />
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2">
                                                <div className="relative w-full">
                                                    <textarea
                                                        value={item.descripcion || ''}
                                                        onChange={(e) => handleRegistroChange(index, 'descripcion', e.target.value)}
                                                        className={`${inputClasses} resize-y min-h-[42px] max-h-[150px] pb-5`}
                                                        placeholder="Ingrese la descripción"
                                                        maxLength="2500"
                                                        rows="1"
                                                    />
                                                    <div className="absolute bottom-1.5 right-2 text-[10px] text-gray-400 dark:text-gray-500 pointer-events-none">
                                                        {(item.descripcion || '').length}/2500
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 align-top">
                                                <select
                                                    value={item.estado || ''}
                                                    onChange={(e) => handleEstadoChange(index, e.target.value)}
                                                    className={inputClasses}
                                                >
                                                    <option value="">— Sin estado —</option>
                                                    <option value="Enviado">Enviado</option>
                                                    <option value="Recibido">Recibido</option>
                                                </select>
                                                {item.estado === 'Recibido' && !item.nro_pv && (
                                                    <label
                                                        className="mt-1 flex items-start gap-1.5 text-[11px] leading-tight text-gray-600 dark:text-gray-300 cursor-pointer"
                                                        title="Marcá esta nota para habilitar el cierre del ciclo 1 de la alerta de Mejoramientos"
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={Boolean(item.solicitud_prorroga)}
                                                            onChange={(e) => handleSolicitudProrrogaChange(index, e.target.checked)}
                                                            className="mt-0.5"
                                                        />
                                                        <span>
                                                            Solicitud de prórroga
                                                            {item.solicitud_prorroga && !item.fecha && (
                                                                <span className="block text-red-600 dark:text-red-400">
                                                                    Cargá la fecha para habilitar el cierre del ciclo 1
                                                                </span>
                                                            )}
                                                        </span>
                                                    </label>
                                                )}
                                            </td>
                                            <td className="border border-gray-300 dark:border-gray-600 px-4 py-2 align-top">
                                                <div className="flex flex-wrap items-center justify-center gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={() => handleEliminarRegistro(index)}
                                                        className="p-2 rounded text-red-600 hover:text-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                                                        title="Eliminar intercambio"
                                                    >
                                                        🗑️
                                                    </button>
                                                    {item.estado === 'Recibido' && Boolean(item.solicitud_prorroga) && (
                                                        <NotaPdfUpload
                                                            codigoObra={codigoObra}
                                                            etapa={etapa}
                                                            notaRef={item.nota_ref}
                                                            nombreArchivo={item.nota_pdf_nombre}
                                                            onRef={(ref) => handleRegistroChange(index, 'nota_ref', ref)}
                                                            onNombreChange={(nombre) => handleRegistroChange(index, 'nota_pdf_nombre', nombre)}
                                                        />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {/* Resumen Premium a la Derecha */}
                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5 flex flex-col justify-between shadow-sm">
                        <div>
                            <h4 className="text-md font-semibold text-gray-800 dark:text-white mb-4">
                                Resumen de Seguimiento
                            </h4>

                            <div className="space-y-3">
                                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 shadow-sm">
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Total Intercambios Cargados:
                                    </span>
                                    <span className="text-lg font-bold text-gray-800 dark:text-white">
                                        {mejorasEtapaI.length}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 shadow-sm">
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Enviados:
                                    </span>
                                    <span className="text-md font-semibold text-blue-600 dark:text-blue-400">
                                        {mejorasEtapaI.filter(h => h.estado === 'Enviado').length}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 shadow-sm">
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Recibidos:
                                    </span>
                                    <span className="text-md font-semibold text-green-600 dark:text-green-400">
                                        {mejorasEtapaI.filter(h => h.estado === 'Recibido').length}
                                    </span>
                                </div>

                                <div className="flex justify-between items-center p-3 bg-white dark:bg-gray-700 rounded border border-gray-200 dark:border-gray-600 shadow-sm">
                                    <span className="text-sm text-gray-600 dark:text-gray-300">
                                        Sin estado:
                                    </span>
                                    <span className="text-md font-semibold text-gray-500 dark:text-gray-400">
                                        {mejorasEtapaI.filter(h => !h.estado).length}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded">
                            <p className="text-xs text-blue-700 dark:text-blue-300 leading-relaxed">
                                💡 <strong>Tip de gestión:</strong> Mantener al día la fecha de cada intercambio permite coordinar los tiempos de recepción y entrega de manera ágil entre los equipos del municipio/ONG y del área de Mejoras.
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TablaMejorasEtapaI;
